import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Spinner, Alert, Modal, Table } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";
import Swal from "sweetalert2";
import "suneditor/dist/css/suneditor.min.css";

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.style.padding = "16px 20px";
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
    }
});

const normalizeProductAttributes = (attributes) => {
    const entries = Array.isArray(attributes)
        ? attributes.map((attribute) => [attribute?.name, attribute?.value])
        : attributes && typeof attributes === "object"
            ? Object.entries(attributes).map(([name, value]) => [
                value && typeof value === "object" ? value.name ?? name : name,
                value && typeof value === "object" ? value.value : value,
            ])
            : [];

    return entries
        .filter(([name]) => name !== null && name !== undefined && String(name).trim() !== "")
        .map(([name, value]) => ({
            name: String(name),
            value: value === null || value === undefined || value === ""
                ? "N/A"
                : typeof value === "object"
                    ? value.name || value.title || "N/A"
                    : String(value),
        }));
};

export default function CheckerProductDetailPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [product, setProduct] = useState(location.state?.item || location.state?.product || null);
    const [loading, setLoading] = useState(!product);
    const [error, setError] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [selectedImg, setSelectedImg] = useState("");
    const productAttributes = normalizeProductAttributes(product?.attributes);

    const handleImageClick = (imgSrc) => {
        setSelectedImg(imgSrc);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedImg("");
    };

    // Delete image handler for array/gallery images
    const handleDeleteImage = (imageId) => {
        Swal.fire({
            title: "Delete Image?",
            text: "Are you sure you want to delete this image?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(
                        `${process.env.REACT_APP_API_URL}/checker/delete-image/${imageId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem("authToken")}`
                            }
                        }
                    );

                    // Update UI state by removing deleted image
                    setProduct((prevProduct) => ({
                        ...prevProduct,
                        images: prevProduct.images.filter((img) => img.id !== imageId)
                    }));

                    Toast.fire({
                        icon: "success",
                        title: "Image deleted successfully!"
                    });
                } catch (err) {
                    console.error("Error deleting image:", err);
                    Toast.fire({
                        icon: "error",
                        title: err.response?.data?.message || "Failed to delete image."
                    });
                }
            }
        });
    };

    const cleanHtmlContent = (htmlString) => {
        if (!htmlString) return "";
        try {
            // 1. Unescape slashes and newlines
            let unescaped = htmlString
                .replace(/\\"/g, '"')
                .replace(/\\\//g, "/")
                .replace(/\\r\\n/g, "")
                .replace(/\\n/g, "")
                .replace(/\\t/g, "");

            // 2. Decode HTML Entities (e.g. &lt;p&gt; -> <p>)
            const textarea = document.createElement("textarea");
            textarea.innerHTML = unescaped;
            let decoded = textarea.value;

            // Double check in case of double encoding
            textarea.innerHTML = decoded;
            decoded = textarea.value;

            // 3. Remove <pre> wrapper if SunEditor adds it
            decoded = decoded.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, "$1");

            return decoded.trim();
        } catch (e) {
            console.error("Error cleaning HTML content:", e);
            return htmlString;
        }
    };

    const fetchProductById = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/checker/products/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            if (resData.success || resData.data) {
                const fetchedData = resData.data;
                if (Array.isArray(fetchedData)) {
                    setProduct(fetchedData[0] || null);
                } else {
                    setProduct(fetchedData || resData);
                }
            } else {
                setError(resData.message || "Failed to fetch product details.");
            }
        } catch (err) {
            console.error("Error fetching checker product details:", err);
            setError(
                err.response?.data?.message || "Failed to load product details."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!product && id) {
            fetchProductById();
        }
    }, [id, product, fetchProductById]);

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_product_details...")}</p>
                </div>
            </PageLayout>
        );
    }

    if (error || !product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Alert variant="danger" className="d-inline-block px-5">
                        {error || t("no product data available")}
                    </Alert>
                    <div>
                        <Button
                            variant="primary"
                            className="mt-3"
                            onClick={() => navigate(-1)}
                        >
                            {t("back to list")}
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <style>
                {`
                    .sun-editor-editable table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                        margin: 15px 0 !important;
                    }
                    .sun-editor-editable table td, 
                    .sun-editor-editable table th {
                        border: 1px solid #dcdcdc !important;
                        padding: 8px 12px !important;
                        vertical-align: middle !important;
                    }
                `}
            </style>

            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("checker product details")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(-1);
                                            }}
                                            className="mc-breadcrumb-link"
                                        >
                                            {t("checker products")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        {t("product details")}
                                    </li>
                                </ul>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => navigate(-1)}
                            >
                                {t("back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col lg={4}>
                    <Card className="mb-4">
                        <Card.Body className="text-center">
                            <h5 className="mt-2 mb-1">{product.title || "N/A"}</h5>
                            <Badge bg="primary" className="mb-3">
                                {product.product_code || "N/A"}
                            </Badge>

                            {product.card_img && (
                                <div className="border-top pt-3 mt-2 text-start">
                                    <small className="text-muted fw-bold d-block mb-2">{t("card image")}:</small>
                                    <div className="text-center">
                                        <img
                                            src={`${process.env.REACT_APP_IMG_URL}${product.card_img}`}
                                            alt="Card Preview"
                                            className="img-fluid rounded border"
                                            style={{ maxHeight: "180px", objectFit: "contain", width: "100%", cursor: "pointer" }}
                                            onClick={() => handleImageClick(`${process.env.REACT_APP_IMG_URL}${product.card_img}`)}
                                        />
                                    </div>
                                </div>
                            )}

                            {product.rejection_reason && (
                                <div className="alert alert-danger py-2 px-3 my-2 text-start">
                                    <strong>{t("rejection reason")}: </strong>
                                    <span>{product.rejection_reason}</span>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {product.images && product.images.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header className="bg-white">
                                <h6 className="mb-0">{t("gallery images")}</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-2">
                                    {product.images.map((imgObj, idx) => {
                                        const fullImgUrl = `${process.env.REACT_APP_IMG_URL}${imgObj.image}`;
                                        return (
                                            <Col key={imgObj.id || idx} xs={6}>
                                                <div className="position-relative">
                                                    <img
                                                        src={fullImgUrl}
                                                        alt={`Gallery ${idx + 1}`}
                                                        className="img-fluid rounded border"
                                                        style={{ height: "100px", width: "100%", objectFit: "cover", cursor: "pointer" }}
                                                        onClick={() => handleImageClick(fullImgUrl)}
                                                    />
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="position-absolute p-0 d-flex align-items-center justify-content-center rounded-circle"
                                                        style={{
                                                            top: "-6px",
                                                            right: "-6px",
                                                            width: "22px",
                                                            height: "22px",
                                                            fontSize: "12px",
                                                            zIndex: 2
                                                        }}
                                                        title={t("delete image")}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteImage(imgObj.id);
                                                        }}
                                                    >
                                                        ✕
                                                    </Button>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                <Col lg={8}>
                    <Card className="mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">{t("product information")}</h5>
                        </Card.Header>
                        <Card.Body>
                            <Table responsive bordered className="align-middle mb-4">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold bg-light" style={{ width: "30%" }}>{t("category")}</td>
                                        <td>
                                            {typeof product.category === "object"
                                                ? product.category?.name
                                                : product.category || "N/A"}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold bg-light">{t("brand")}</td>
                                        <td>
                                            {typeof product.brand === "object"
                                                ? product.brand?.name
                                                : product.brand || "N/A"}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td className="fw-bold bg-light">{t("selling style")}</td>
                                        <td>{product.selling_style || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold bg-light">{t("quantity")}</td>
                                        <td>{product.product_qty ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold bg-light">{t("sell price dollar")}</td>
                                        <td>${product.sell_price_dollar ?? 0}</td>
                                    </tr>
                                    {product.selling_style === "Auction" && (
                                        <tr>
                                            <td className="fw-bold bg-light">{t("Auction Min Price ($)")}</td>
                                            <td>
                                                {product.pricing?.auction_min_price ?? product.auction_min_price ?? "N/A"}
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td className="fw-bold bg-light">{t("shipping charges dollar")}</td>
                                        <td>${product.shipping_charges_dollar ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold bg-light">{t("product condition")}</td>
                                        <td>{product.product_condition || "N/A"}</td>
                                    </tr>
                                </tbody>
                            </Table>

                            {productAttributes.length > 0 && (
                                <div className="mb-4">
                                    <h6 className="fw-bold mb-3">{t("product attributes")}:</h6>
                                    <Table responsive bordered className="align-middle mb-0">
                                        <tbody>
                                            {productAttributes.map((attribute, index) => (
                                                <tr key={`${attribute.name}-${index}`}>
                                                    <td className="fw-bold bg-light" style={{ width: "30%" }}>
                                                        {attribute.name}
                                                    </td>
                                                    <td>{attribute.value}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {product.short_description && (
                                <div className="mb-4">
                                    <h6 className="fw-bold">{t("short description")}:</h6>
                                    <div
                                        className="sun-editor-editable text-muted mb-0 border p-3 rounded bg-light"
                                        dangerouslySetInnerHTML={{
                                            __html: cleanHtmlContent(product.short_description),
                                        }}
                                    />
                                </div>
                            )}

                            {product.description && (
                                <div className="mb-0">
                                    <h6 className="fw-bold">{t("Description")}:</h6>
                                    <div
                                        className="sun-editor-editable text-muted mb-0 border p-3 rounded bg-light"
                                        dangerouslySetInnerHTML={{
                                            __html: cleanHtmlContent(product.description),
                                        }}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton className="p-4"></Modal.Header>
                <Modal.Body className="text-center p-2">
                    <img
                        src={selectedImg}
                        alt="Product Preview"
                        className="img-fluid rounded"
                        style={{ maxHeight: "80vh", objectFit: "contain" }}
                    />
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}